import { useId, useState, type MouseEvent } from "react";
import { useTranslation } from "@privacy/translations";
import { Avatar, Box, Menu, MenuItem } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useUserInfo } from "../../api/userInfo";
import { ChevronsUpDownIcon, LogOutIcon, UserIcon } from "../../shared/icons";
import { useAuthUser } from "../../auth/AuthProvider";
import { SidebarItem } from "./SidebarMenuItem";
import { useSidebar } from "./Sidebar";
import { SIDEBAR_PADDING_X } from "./sidebarConstants";
import { appConfig } from "../../appConfig";
import { HACK_alignIconWithLabelBox } from "@privacy/ui";

const AVATAR_SIZE = 30;
const AVATAR_PADDING = 32 - AVATAR_SIZE;

export const ProfileMenu = () => {
  const { t } = useTranslation();
  const { isSidebarExpanded } = useSidebar();
  const auth = useAuth();
  const authUser = useAuthUser();
  const userInfo = useUserInfo();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const fullName = `${userInfo.firstName} ${userInfo.lastName}`;
  const initials = `${userInfo.firstName.charAt(0)}${userInfo.lastName.charAt(0)}`;

  return (
    <>
      <Box
        sx={{
          display: isSidebarExpanded ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          px: `${AVATAR_PADDING}px`,
          py: 1.5,
        }}
      >
        <Avatar
          sx={{
            borderRadius: 1,
            mx: SIDEBAR_PADDING_X,
            bgcolor: theme => theme.palette.primary["100"],
            color: theme => theme.palette.primary.dark,
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontWeight: 600,
            fontSize: theme => theme.typography.pxToRem(12),
          }}
          src={authUser.picture}
          alt={fullName}
        >
          {initials}
        </Avatar>
      </Box>

      <SidebarItem
        sx={{ ...(!isSidebarExpanded && { display: "none" }) }}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        component="button"
        label={fullName}
        subLabel={userInfo.email}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
        renderEndIcon={props => <ChevronsUpDownIcon {...props} />}
      />
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        disableScrollLock
        slotProps={{
          paper: {
            onMouseLeave: () => setAnchorEl(null),
            sx: { width: "250px" },
          },
        }}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem component="a" href={appConfig.env.ACCOUNT_SETTINGS_URL} target="_blank" rel="noopener noreferrer">
          <UserIcon />
          <HACK_alignIconWithLabelBox>{t("Sidebar.MyAccount")}</HACK_alignIconWithLabelBox>
        </MenuItem>
        <MenuItem onClick={() => auth.signoutRedirect()}>
          <LogOutIcon />
          {t("Sidebar.Logout")}
        </MenuItem>
      </Menu>
    </>
  );
};
